import { Component, OnInit, ChangeDetectionStrategy, signal } from '@angular/core';
import { Router } from '@angular/router';
import { GameQuery } from 'src/app/state/gameState/game.query';
import { GameService } from 'src/app/state/gameState/game.service';
import { UserService } from 'src/app/state/userState/user.service';
import { ITopic } from '../../../models/api.model';
import { createTimeline } from 'animejs';
import { AnimationItem } from 'lottie-web';
import { AnimationOptions } from 'ngx-lottie';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { catchError, take } from 'rxjs/operators';
import { TranslateService } from '@ngx-translate/core';

@UntilDestroy()
@Component({
  selector: 'app-proposal-page',
  templateUrl: './proposal.page.html',
  styleUrls: ['./proposal.page.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class ProposalPage implements OnInit {
  options: AnimationOptions;
  public topic = signal<ITopic | null>(null);
  public page = signal(0);
  public allPages: number;
  public title: string;
  public country: string;
  isInformational = signal(false);
  isSecreteriat = signal(false);
  representativePath: string;
  flag = signal(false);
  animations: any[] = [];
  timerId: any;
  private ai: AnimationItem;
  constructor(
    private gameService: GameService,
    private gameQuery: GameQuery,
    private router: Router,
    private userService: UserService,
    private translateService: TranslateService
  ) {
    this.translateService.onLangChange
      .pipe(
        untilDestroyed(this),
        catchError((err) => {
          console.log('COULDNT TRANSLATE ', err);
          throw err;
        })
      )
      .subscribe(() => {
        this.narration();
      });
  }

  ngOnInit() {
    this.gameQuery.topics$.pipe(untilDestroyed(this)).subscribe((item) => {
      if (!item.length) {
        return;
      }
      const foundTopic = item.find(
        (topic) => topic.id === this.gameQuery.getTopicId()
      );
      this.topic.set(foundTopic);
      this.isInformational.set(this.gameQuery.getCurrentTopicIsInformational());
      this.isSecreteriat.set(this.gameQuery.getCurrentTopicSpeakerIsSecreteriat());
      this.allPages = foundTopic.dimension.length + 1;
      this.gameService.addTopic(foundTopic);
      this.gameQuery.representative$
        .pipe(untilDestroyed(this), take(1))
        .subscribe((path) => {
          this.representativePath = path;
          const animeRepresentativePath = path.slice(0, path.indexOf('.'));
          const jsonTxt = '.json';
          this.options = {
            path:
              '/assets/testAnimations/representatives/' +
              animeRepresentativePath +
              jsonTxt,
          };
        });
    });
  }

  ionViewDidEnter() {
    this.narration();
  }

  nextPage() {
    if (this.page() === this.allPages) {
      this.gameService.updateProposalTour(true);
      this.router.navigate(['/presentation_proposal']);
    } else {
      this.page.update(p => p + 1);
      this.narration();
    }
  }

  previousPage() {
    this.page.update(p => p - 1);
    this.narration();
  }

  narration() {
    if (this.page() !== 0) {
      this.flag.set(false);
    }
    const startAnimation = (page: number) => {
      this.animations.forEach((anim) => anim.pause());
      this.animations = [];
      this.ai?.play();
      this.animations.push(
        createTimeline({ loop: false }).add(`.anim-a .word`, {
          scale: [4, 1],
          opacity: [0, 1],
          translateZ: 0,
          ease: 'outExpo',
          delay: (el, i) => {
            this.setSpeed((120 * i) / 1000);
            return 120 * i;
          },
          onComplete: () => {
            createTimeline({ loop: false }).add(`.anim-b .word`, {
              scale: [4, 1],
              opacity: [0, 1],
              translateZ: 0,
              ease: 'outExpo',
              delay: (el, i) => {
                this.setSpeed((120 * i) / 1000);
                return 120 * i;
              },
              onComplete: () => {
                if (page === 0) {
                  this.ai.stop();
                  return;
                }
                createTimeline({ loop: false }).add(`.anim-c .word`, {
                  scale: [4, 1],
                  opacity: [0, 1],
                  translateZ: 0,
                  ease: 'outExpo',
                  delay: (el, i) => {
                    this.setSpeed((120 * i) / 1000);
                    return 120 * i;
                  },
                  onComplete: () => {
                    this.flag.set(true);
                    createTimeline({ loop: false }).add(`.anim-d .word`, {
                      scale: [4, 1],
                      opacity: [0, 1],
                      translateZ: 0,
                      ease: 'outExpo',
                      delay: (el, i) => {
                        this.setSpeed((120 * i) / 1000);
                        return 120 * i;
                      },
                      onComplete: () => {
                        this.ai.stop();
                      },
                    });
                  },
                });
              },
            });
          },
        })
      );
    };
    setTimeout(() => startAnimation(this.page()), 200);
  }

  animationCreated(animationItem: AnimationItem): void {
    this.ai = animationItem;
  }

  setSpeed(num: number) {
    if (num > 0.8) {
      num = 0.8;
    }
    this.ai.setSpeed(num);
  }
}
